/**
 * The phone chrome's pure decisions (card t_6a634e11): which route walks up to
 * which parent, which route is a section root, and when a finished gesture is a
 * swipe rather than a scroll.
 *
 * Kept apart from `composables/useMobileBack.ts` on purpose, exactly like
 * `utils/displayParamsHost.ts`: the composable drags the Pinia stores (and with
 * them `import.meta.env`) in, which the Node test runner cannot load — while the
 * table and the thresholds below are the part a regression would silently move.
 * The composable re-exports everything here, so callers keep one import.
 */

/** Distance from a screen edge a gesture may start in to count as an edge swipe. */
export const SWIPE_EDGE_PX = 24;
/** Minimum horizontal travel before a gesture is a swipe at all. */
export const SWIPE_MIN_DX = 12;
/** |dx| must beat |dy| by this factor, so a vertical drag never navigates. */
export const SWIPE_AXIS_RATIO = 1.5;

/** What a finished gesture should do. */
export type SwipeVerdict = 'none' | 'open-menu' | 'back';

/** Classify a finished pointer gesture (thresholds: see the constants above). */
export function classifyEdgeSwipe(startX: number, dx: number, dy: number, viewportWidth: number): SwipeVerdict {
  if (Math.abs(dx) < SWIPE_MIN_DX) return 'none';
  if (Math.abs(dx) <= Math.abs(dy) * SWIPE_AXIS_RATIO) return 'none';
  if (dx > 0 && startX <= SWIPE_EDGE_PX) return 'open-menu';
  if (dx < 0 && viewportWidth - startX <= SWIPE_EDGE_PX) return 'back';
  return 'none';
}

/**
 * Where "back" goes from `path`, or `null` when the route is already a section
 * root (then the shell paints no arrow, and the hardware key is free to exit on
 * the launcher).
 *
 * The table is explicit rather than "drop the last path segment", because
 * segments are not levels here: `/settings/integrations/:id/datapoints` and
 * `/settings/users` are both below the settings directory, and an id is not a
 * page. Every parent named below is a route that renders on its own — a
 * `/settings` root that resolves through `_redirect` to the *last* settings page
 * would be a dead back button, so settings leaves go to the hub dashboard, which
 * is the hub's own root.
 */
export function resolveMobileParent(path: string): string | null {
  const clean = path.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';

  // Every hub route exists twice in the router: `/h/<hubId>/…` (cloud, or a hub
  // picked by id) and unscoped (`/rooms/overview`, `/dashboards/<id>`, …) — the
  // local-mode / active-hub shape, which is the one the app actually runs in on
  // the owner's tablet. Same table and same walk for both; only the prefix the
  // parent carries differs, so the table below is written against the tail.
  const hub = clean.match(/^\/h\/([^/]+)(\/.*)?$/);
  const rest = hub ? (hub[2] ?? '') : clean;
  const at = (suffix: string) => `${hub ? `/h/${hub[1]}` : ''}${suffix}`;

  // Rooms: the room page is a level below the rooms overview. `overview` and the
  // directory's own `_redirect` shell are both roots.
  const room = rest.match(/^\/rooms\/([^/]+)$/);
  if (room) {
    return room[1] === 'overview' || room[1] === '_redirect' ? null : at('/rooms/overview');
  }

  // People detail (its list lives in the settings directory now).
  if (/^\/users\/[^/]+$/.test(rest)) return at('/settings/users');
  if (rest === '/users') return at('/settings');

  // A camera's live view is a level below the cameras list.
  if (/^\/cameras\/[^/]+\/live$/.test(rest)) return at('/cameras');

  // Legacy integration routes redirect into settings; a tab or a detail page
  // inside one walks back to the list it belongs to.
  if (/^\/(?:settings\/)?integrations\/[^/]+\/.+$/.test(rest)) return at('/settings/integrations');

  // Any other settings sub-page: the hub's own root page. `_redirect` is not a
  // sub-page — it is the directory shell that `/settings` itself redirects
  // into, so it stays a root (and the back arrow stays hidden there). Nor is
  // `/settings` itself a sub-page of anything.
  if (/^\/settings\/_redirect(\/.*)?$/.test(rest)) return null;
  if (/^\/settings\/[^/]+/.test(rest)) return at('/dashboard');

  // Launcher pages (AppTopBar chrome) — never hub-scoped.
  if (/^\/me\/.+/.test(rest)) return '/me';
  if (/^\/app-settings\/.+/.test(rest)) return '/app-settings';
  const service = rest.match(/^\/services\/([^/]+)\/(.+)$/);
  if (service) return `/services/${service[1]}`;
  if (/^\/services\/[^/]+$/.test(rest)) return '/services';
  if (/^\/local\/.+/.test(rest)) return '/local';

  return null;
}

/** True when the path is a section root: nothing above it to go back to. */
export function isSectionRoot(path: string): boolean {
  return resolveMobileParent(path) === null;
}

/** The launcher root: the one page where the hardware back may close the app. */
export function isLauncherRoot(path: string): boolean {
  const clean = path.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  return clean === '/' || clean === '/home';
}
